<template>
  <v-app>
    <!-- Barra de navegação principal fixa -->
    <v-app-bar app color="white" dark density="compact">
      <img src="/logo.png" alt="Logo" class="navbar-logo mr-3" />
      <v-toolbar-title>{{ $t('home.title') }}</v-toolbar-title>
      <v-spacer></v-spacer>

      <!-- Nearest deadline countdown (toolbar) -->
      <v-chip
        v-if="nearestDeadline"
        :color="deadlineColor(nearestDeadline.level)"
        size="small"
        label
        variant="flat"
        :class="['mr-1', { 'deadline-blink': nearestDeadline.msRemaining < 3600000 }]"
        :title="$t('home.deadlineAlerts.title')"
        @click="goToDeadlineProject(nearestDeadline)"
      >
        <v-icon start size="x-small">mdi-clock-alert-outline</v-icon>
        {{ formatRemaining(nearestDeadline.msRemaining) }}
      </v-chip>

      <!-- Verifiche AI in corso, di tutti gli utenti: il registro sta nel Backend -->
      <v-menu :close-on-content-click="false">
        <template v-slot:activator="{ props }">
          <v-badge
            :content="activeVerificationRuns.length"
            :model-value="activeVerificationRuns.length > 0"
            color="indigo"
            offset-x="6"
            offset-y="6"
          >
            <v-btn icon v-bind="props" size="small" variant="text" :title="$t('home.activeRuns.title')">
              <v-progress-circular
                v-if="activeVerificationRuns.length"
                indeterminate size="18" width="2" color="indigo"
              />
              <v-icon v-else color="grey">mdi-robot-outline</v-icon>
            </v-btn>
          </v-badge>
        </template>
        <v-card min-width="320" max-width="460">
          <v-list density="compact">
            <v-list-subheader>{{ $t('home.activeRuns.title') }}</v-list-subheader>
            <v-list-item v-if="activeVerificationRuns.length === 0">
              <v-list-item-title class="text-grey">{{ $t('home.activeRuns.empty') }}</v-list-item-title>
            </v-list-item>
            <v-list-item
              v-for="r in activeVerificationRuns"
              :key="r.projectId + r.verificationId"
              @click="goToVerificationRun(r)"
            >
              <template v-slot:prepend>
                <v-icon :color="r.mine ? 'indigo' : 'grey'" size="small">mdi-robot-outline</v-icon>
              </template>
              <v-list-item-title class="font-weight-medium">
                {{ r.projectId }} <span class="text-grey">· {{ r.label }}</span>
              </v-list-item-title>
              <v-list-item-subtitle>
                {{ r.mine ? $t('home.activeRuns.mine') : r.owner }}
                <span v-if="r.stepCount"> — {{ $t('home.activeRuns.step', { n: r.stepIndex, total: r.stepCount }) }}</span>
                — {{ $t('home.activeRuns.since', { minutes: runAge(r) }) }}
              </v-list-item-subtitle>
            </v-list-item>
          </v-list>
        </v-card>
      </v-menu>

      <!-- Chat IA sullo stato dei processi (solo admin) -->
      <v-btn
        v-if="isAdmin"
        icon
        size="small"
        variant="text"
        :title="$t('aiChat.open')"
        @click="adminChatStore.toggle()"
      >
        <v-icon :color="adminChatStore.open ? 'indigo' : 'grey'">mdi-chat-processing-outline</v-icon>
      </v-btn>

      <!-- Global deadline signal (always present) -->
      <v-menu :close-on-content-click="false">
        <template v-slot:activator="{ props }">
          <v-badge
            :content="deadlineAlerts.length"
            :model-value="deadlineAlerts.length > 0"
            :color="worstAlertLevel === 'critical' ? 'red' : 'orange'"
            offset-x="6"
            offset-y="6"
          >
            <v-btn icon v-bind="props" size="small" variant="text" :title="$t('home.deadlineAlerts.title')">
              <v-icon :color="deadlineAlerts.length ? (worstAlertLevel === 'critical' ? 'red' : 'orange') : 'grey'">
                {{ deadlineAlerts.length ? 'mdi-bell-alert' : 'mdi-bell-outline' }}
              </v-icon>
            </v-btn>
          </v-badge>
        </template>
        <v-card min-width="320" max-width="440">
          <v-list density="compact">
            <v-list-subheader>{{ $t('home.deadlineAlerts.title') }}</v-list-subheader>
            <v-list-item v-if="deadlineAlerts.length === 0">
              <v-list-item-title class="text-grey">{{ $t('home.deadlineAlerts.empty') }}</v-list-item-title>
            </v-list-item>
            <v-list-item
              v-for="d in deadlineAlerts"
              :key="d.idProject"
              @click="goToDeadlineProject(d)"
            >
              <template v-slot:prepend>
                <v-icon :color="deadlineColor(d.level)" size="small">mdi-clock-alert-outline</v-icon>
              </template>
              <v-list-item-title class="font-weight-medium">
                {{ d.idProject }}<span v-if="d.reference" class="text-grey"> · {{ d.reference }}</span>
              </v-list-item-title>
              <v-list-item-subtitle>
                {{ deadlineStatusDescr(d) }} — <span :class="d.level === 'overdue' ? 'text-red' : ''">{{ formatRemaining(d.msRemaining) }}</span>
              </v-list-item-subtitle>
            </v-list-item>
          </v-list>
        </v-card>
      </v-menu>

      <!-- Test Date Override (test mode only) -->
      <v-menu v-if="isTestModeAvailable" :close-on-content-click="false">
        <template v-slot:activator="{ props }">
          <v-btn
            v-bind="props"
            size="small"
            variant="text"
            :color="testDate ? 'warning' : 'grey'"
            prepend-icon="mdi-calendar-clock"
          >
            {{ testDate || $t('home.testDate.real') }}
          </v-btn>
        </template>
        <v-card min-width="260" class="pa-3">
          <div class="text-caption text-medium-emphasis mb-2">{{ $t('home.testDate.title') }}</div>
          <v-text-field
            type="datetime-local"
            :model-value="testDate"
            @update:model-value="setTestDate"
            density="compact"
            hide-details
          />
          <v-btn
            class="mt-2"
            size="small"
            variant="tonal"
            color="grey"
            block
            :disabled="!testDate"
            @click="setTestDate(null)"
          >
            {{ $t('home.testDate.reset') }}
          </v-btn>
        </v-card>
      </v-menu>

      <!-- User Name -->
      <span class="mr-3">{{ username }}</span>
      
      <!-- Refresh Button (only on dashboard) -->
      <v-btn
        v-if="currentRoute === 'dashboard'"
        @click="refreshDashboard"
        :loading="isRefreshing"
        size="small"
        prepend-icon="mdi-refresh"
        variant="text"
      >
        {{ $t('dashboard.refresh') }}
      </v-btn>
      
      <!-- Stato del canale in tempo reale. Senza questo un hub caduto sarebbe indistinguibile da
           "non sta succedendo niente": tolti i timer, non c'e' piu' nulla che si muova da solo. -->
      <v-tooltip bottom>
        <template v-slot:activator="{ props }">
          <v-icon v-bind="props" :color="liveColor" class="mr-2" size="small">
            {{ liveState === 'online' ? 'mdi-flash' : 'mdi-flash-off' }}
          </v-icon>
        </template>
        <span>{{ $t(`home.live.${liveState}`) }}</span>
      </v-tooltip>

      <!-- LocalBackend Status Indicator -->
      <v-tooltip bottom>
        <template v-slot:activator="{ props }">
          <v-icon
            v-bind="props"
            :color="localBackendConnected ? 'success' : 'error'"
            class="mr-2"
          >
            {{ localBackendConnected ? 'mdi-lan-connect' : 'mdi-lan-disconnect' }}
          </v-icon>
        </template>
        <span>{{ !localBackendStore.hasShell ? $t('home.localShellMissing') : (localBackendConnected ? $t('home.localBackendConnected') : $t('home.localBackendDisconnected')) }}</span>
      </v-tooltip>
      
      <!-- Menu -->
      <v-menu>
        <template v-slot:activator="{ props }">
          <v-btn icon v-bind="props">
            <v-icon>mdi-menu</v-icon>
          </v-btn>
        </template>
        <v-list>
          <v-list-item @click="navigateTo('dashboard')">
            <template v-slot:prepend>
              <v-icon>mdi-view-dashboard</v-icon>
            </template>
            <v-list-item-title>{{ $t('nav.dashboard') }}</v-list-item-title>
          </v-list-item>
          
          <v-list-item @click="navigateTo('archive')">
            <template v-slot:prepend>
              <v-icon>mdi-archive</v-icon>
            </template>
            <v-list-item-title>{{ $t('nav.archive') }}</v-list-item-title>
          </v-list-item>
          
          <v-list-item @click="navigateTo('help')">
            <template v-slot:prepend>
              <v-icon>mdi-help-circle</v-icon>
            </template>
            <v-list-item-title>{{ $t('nav.help') }}</v-list-item-title>
          </v-list-item>
          
          <v-list-item @click="navigateTo('adm-config')">
            <template v-slot:prepend>
              <v-icon>mdi-cog</v-icon>
            </template>
            <v-list-item-title>{{ $t('nav.config') }}</v-list-item-title>
          </v-list-item>

          <v-list-item v-if="isAdmin" @click="navigateTo('workload')">
            <template v-slot:prepend>
              <v-icon>mdi-account-clock-outline</v-icon>
            </template>
            <v-list-item-title>{{ $t('nav.workload') }}</v-list-item-title>
          </v-list-item>

          <v-list-item v-if="isAdmin" @click="navigateTo('ai-admin')">
            <template v-slot:prepend>
              <v-icon>mdi-robot-outline</v-icon>
            </template>
            <v-list-item-title>{{ $t('nav.aiAdmin') }}</v-list-item-title>
          </v-list-item>

          <v-list-item v-if="userRole === 'superadmin'" @click="navigateTo('users')">
            <template v-slot:prepend>
              <v-icon>mdi-account-group</v-icon>
            </template>
            <v-list-item-title>{{ $t('nav.users') }}</v-list-item-title>
          </v-list-item>

          <v-list-item v-if="userRole === 'superadmin'" @click="navigateTo('siscomex')">
            <template v-slot:prepend>
              <v-icon>mdi-api</v-icon>
            </template>
            <v-list-item-title>{{ $t('nav.siscomex') }}</v-list-item-title>
          </v-list-item>
          
          <v-divider></v-divider>
          
          <v-list-item @click="logout">
            <template v-slot:prepend>
              <v-icon>mdi-logout</v-icon>
            </template>
            <v-list-item-title>{{ $t('nav.logout') }}</v-list-item-title>
          </v-list-item>
        </v-list>
      </v-menu>
    </v-app-bar>

    <AdminAiChat v-if="isAdmin" />

    <!-- Conteúdo principal -->
    <v-main>
      <!-- Browser puro, senza la shell desktop: le funzioni sulle cartelle non ci sono, e non è un errore -->
      <v-alert
        v-if="!localBackendStore.hasShell"
        type="info"
        variant="tonal"
        closable
        class="ma-4"
      >
        <v-alert-title>{{ $t('home.localShellMissing') }}</v-alert-title>
        <div>{{ $t('home.localShellMissingText') }}</div>
      </v-alert>

      <!-- Configuration Error Alert -->
      <v-alert
        v-if="localBackendStore.configurationError"
        type="error"
        variant="tonal"
        closable
        class="ma-4"
      >
        <v-alert-title>{{ $t('home.configurationError') }}</v-alert-title>
        <div>{{ localBackendStore.configurationError }}</div>
        <div v-if="localBackendStore.missingPaths.length > 0" class="mt-2">
          <strong>{{ $t('home.missingPaths') }}:</strong>
          <ul>
            <li v-for="(path, index) in localBackendStore.missingPaths" :key="index">
              {{ path.name }}: {{ path.reason }}
              <span v-if="path.path" class="text-grey">({{ path.path }})</span>
            </li>
          </ul>
        </div>
        <v-btn
          color="primary"
          variant="text"
          @click="navigateTo('adm-config')"
          class="mt-2"
        >
          {{ $t('home.goToConfiguration') }}
        </v-btn>
      </v-alert>

      <!-- LocalBackend vecchio: funziona, ma salva male i nomi lunghi (vedi localBackend.js) -->
      <v-alert
        v-if="localBackendStore.isOutdated"
        type="warning"
        variant="tonal"
        closable
        class="ma-4"
      >
        <v-alert-title>{{ $t('home.localBackendOutdated') }}</v-alert-title>
        <div>{{ $t('home.localBackendOutdatedText') }}</div>
      </v-alert>

      <!-- Configuration Warnings -->
      <v-alert
        v-if="localBackendStore.warnings.length > 0"
        type="warning"
        variant="tonal"
        closable
        class="ma-4"
      >
        <v-alert-title>{{ $t('home.configurationWarnings') }}</v-alert-title>
        <ul>
          <li v-for="(warning, index) in localBackendStore.warnings" :key="index">
            {{ warning }}
          </li>
        </ul>
      </v-alert>
      
      <router-view />
    </v-main>

    <!-- Canale caduto: si blocca tutto. Senza aggiornamenti in tempo reale si lavorerebbe su dati
         fermi, e due utenti sullo stesso processo si sovrascriverebbero a vicenda senza accorgersene.
         Non è chiudibile di proposito: sparisce da sola se la connessione torna. -->
    <v-dialog v-model="showDisconnected" persistent max-width="480" :scrim="true">
      <v-card>
        <v-card-title class="d-flex align-center text-wrap">
          <v-icon color="error" class="mr-2">mdi-lan-disconnect</v-icon>
          {{ $t('home.disconnected.title') }}
        </v-card-title>
        <v-card-text>
          <p class="mb-2">{{ $t('home.disconnected.message') }}</p>
          <p class="text-medium-emphasis text-body-2 mb-0">{{ $t('home.disconnected.retrying') }}</p>
          <v-alert
            v-if="activeVerificationRuns.some(r => r.mine)"
            type="warning"
            variant="tonal"
            density="compact"
            class="mt-3"
          >
            {{ $t('home.disconnected.runWarning') }}
          </v-alert>
        </v-card-text>
        <v-card-actions>
          <v-progress-circular indeterminate size="20" width="2" color="grey" class="ml-2" />
          <v-spacer />
          <v-btn color="primary" variant="flat" prepend-icon="mdi-reload" @click="reloadPage">
            {{ $t('home.disconnected.reload') }}
          </v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>
  </v-app>
</template>

<script setup>
import { useRouter, useRoute } from 'vue-router'
import { useAuthStore } from '../stores/auth.js'
import { useLocalBackendStore } from '../stores/localBackend.js'
import { useProjectStore } from '../stores/projects.js'
import { useVerificationRunsStore } from '../stores/verificationRuns.js'
import { useAdminChatStore } from '../stores/adminChat.js'
import AdminAiChat from '../components/AdminAiChat.vue'
import { useDeadlines } from '../composables/useDeadlines.js'
import { computed, onMounted, onUnmounted, watch, ref } from 'vue'
import { testDate, setTestDate, isTestModeAvailable } from '../utils/dateUtils.js'
import { startLive, stopLive, liveState, liveDown } from '../realtime/liveConnection.js'
import { onLiveEvent, resyncAll } from '../realtime/handlers.js'

const router = useRouter()
const route = useRoute()
const authStore = useAuthStore()
const localBackendStore = useLocalBackendStore()
const projectStore = useProjectStore()
const verificationRunsStore = useVerificationRunsStore()
const adminChatStore = useAdminChatStore()
const { deadlineAlerts, worstAlertLevel, deadlineColor, formatRemaining } = useDeadlines()
const isRefreshing = ref(false)

// Scadenza più vicina (deadlineAlerts è già ordinata per urgenza)
const nearestDeadline = computed(() => deadlineAlerts.value[0] || null)

// Descrizione status per una voce di alert
const deadlineStatusDescr = (alert) => {
  const info = projectStore.getStatusInfo(String(alert.activeStatus), alert.projectType)
  return info?.descr || ''
}

// Apre il progetto dalla lista deadline (porta alla dashboard)
const goToDeadlineProject = (alert) => {
  router.push({ name: 'dashboard', query: { open: alert.idProject } })
}

// Verifiche in corso (di chiunque): il registro vive nel Backend, qui lo si interroga a intervalli.
const activeVerificationRuns = computed(() => verificationRunsStore.activeLocks)

const liveColor = computed(() => ({
  online: 'success',
  connecting: 'warning',
}[liveState.value] || 'grey'))

// v-model scrivibile ma di fatto governato da liveDown: il dialogo non si chiude a mano, si chiude
// quando il canale torna.
const showDisconnected = computed({
  get: () => liveDown.value,
  set: () => {}
})

const reloadPage = () => window.location.reload()

const runAge = (run) => {
  if (!run?.startedAt) return '0'
  return String(Math.max(0, Math.round((Date.now() - new Date(run.startedAt).getTime()) / 60000)))
}

// Apre il processo e, con lui, la finestra di quella verifica: ProjectsTable gestisce gia ?open=,
// il form legge ?verification=.
const goToVerificationRun = (run) => {
  router.push({ name: 'dashboard', query: { open: run.projectId, verification: run.verificationId } })
}

const currentRoute = computed(() => route.name)
const localBackendConnected = computed(() => localBackendStore.isConnected)

// Estrai il nome utente dal token JWT
const username = computed(() => {
  const decoded = authStore.decodeToken()
  if (decoded) {
    // Il token JWT potrebbe contenere 'sub', 'username', 'name', etc.
    return decoded.sub || decoded.username || decoded.name || 'User'
  }
  return authStore.user || 'User'
})

const userRole = computed(() => authStore.user?.role)
const isAdmin = computed(() => userRole.value === 'admin' || userRole.value === 'superadmin')

const navigateTo = (routeName) => {
  router.push({ name: routeName })
}

const logout = async () => {
  authStore.logout()
  router.push('/login')
}

// Funzione per refresh della dashboard
const refreshDashboard = () => {
  // Emette un evento che la dashboard può ascoltare
  window.dispatchEvent(new CustomEvent('dashboard-refresh'))
}

// Check connection on mount and when navigating to dashboard
onMounted(() => {
  localBackendStore.checkConnection()
  // Segnale deadline globale: disponibile da subito, su qualunque schermata. Da qui in poi lo
  // aggiornano gli eventi, non piu' un giro ogni cinque minuti.
  projectStore.loadDeadlines()
  // Registro delle verifiche in corso: Home e la cornice di tutte le schermate, quindi si registra
  // una volta sola e vale ovunque.
  verificationRunsStore.startPolling()
  // Il canale in tempo reale vive quanto la sessione, non quanto la singola schermata.
  startLive({ onEvent: onLiveEvent, resync: resyncAll })
})

onUnmounted(() => {
  verificationRunsStore.stopPolling()
  stopLive()
})

// Check connection when route changes to dashboard
watch(() => route.name, (newRoute) => {
  if (newRoute === 'dashboard') {
    localBackendStore.checkConnection()
  }
})
</script>

<style scoped>
.v-btn--active {
  background-color: rgba(255, 255, 255, 0.2) !important;
}

.navbar-logo {
  max-height: 60px;
  width: auto;
}

/* Lampeggio del countdown quando manca meno di un'ora */
.deadline-blink {
  animation: deadline-blink 1s steps(1, end) infinite;
}

@keyframes deadline-blink {
  50% {
    opacity: 0.25;
  }
}
</style>